import { imageFileResizer } from '@peacechen/react-image-file-resizer';
import type { Feature, GeoJsonProperties, Geometry, Polygon } from 'geojson';
import html2canvas from 'html2canvas';
import { GeoJSONSource, Map, Point, PointLike } from 'maplibre-gl';

import {
  aerialMapboxBackgroundLayerSourceId,
  aerialMapboxLayerSourceId,
  fullScreenPolygon,
} from '@/features/navigation/topNav/layers/aerialMapLayers';
import type { WindowDimensions } from '@/hooks/useWindowDimensions';

export enum Orientation {
  LANDSCAPE = 'landscape',
  PORTRAIT = 'portrait',
}

export const aerialMapAspectRatio = 3 / 4; // Set 3:4 aspect ratio for the box

// These are the actual width and height needed for ellicott
export const aerialMapDimensions = {
  [Orientation.LANDSCAPE]: {
    height: 480,
    width: 776,
  },
  [Orientation.PORTRAIT]: {
    height: 660,
    width: 596,
  },
};

export const convertImageToFile = async (
  dataUrl: string,
  selectedOrientation: Orientation
) => {
  const maxWidth = aerialMapDimensions[selectedOrientation].width;
  const maxHeight = aerialMapDimensions[selectedOrientation].height;

  return fetch(dataUrl)
    .then((res) => res.blob())
    .then(async (blob) => {
      const tempImageAsFile = new File([blob], 'aerial_map.webp', {
        type: 'image/webp',
      });

      const resizeFile = await imageFileResizer({
        compressFormat: 'webp',
        file: tempImageAsFile,
        maxHeight,
        maxWidth,
        minHeight: maxHeight,
        minWidth: maxWidth,
        outputType: 'file',
        quality: 100,
        rotation: 0,
      });

      return resizeFile as File;
    });
};

export const getNormalizedScalingFactor = (
  windowDimensions: WindowDimensions,
  selectedOrientation: Orientation
) => {
  const screenWidth = windowDimensions.windowWidth || window.innerWidth;
  const screenHeight = windowDimensions.windowHeight || window.innerHeight;

  const pixelRatio = window.devicePixelRatio || 1;

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 1.0;

  const normalizedScreenSize = Math.sqrt(screenWidth * screenHeight);

  const baseFactor = normalizedScreenSize / 1000;

  let scalingFactor = baseFactor * pixelRatio;

  if (selectedOrientation === Orientation.LANDSCAPE) {
    scalingFactor *= 0.5;
  } else {
    scalingFactor *= 0.9;
  }
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, scalingFactor));
};

export const addPreviewBoxToMap = (
  map: Map,
  normalizedScallingFacter,
  selectedOrientation
) => {
  if (!map) return null;

  const canvas = map.getCanvas();
  const canvasWidth = canvas.clientWidth;
  const canvasHeight = canvas.clientHeight;

  // Determine the smaller dimension to fit the rectangle within the map view
  let rectangleWidth: number;
  let rectangleHeight: number;

  // If the height is smaller, calculate based on height
  if (canvasHeight < canvasWidth) {
    rectangleHeight = canvasHeight * 0.3; // 30% of canvas height
    rectangleWidth = rectangleHeight * aerialMapAspectRatio;
  } else {
    // Otherwise, calculate based on width
    rectangleWidth = canvasWidth * 0.3; // 30% of canvas width
    rectangleHeight = rectangleWidth / aerialMapAspectRatio;
  }

  // Adjust for landscape orientation
  if (selectedOrientation === Orientation.LANDSCAPE) {
    const temp = rectangleWidth;
    rectangleWidth = rectangleHeight;
    rectangleHeight = temp;
  }

  // Get the center of the map in screen coordinates
  const center = map.getCenter();
  const centerPoint = map.project([center.lng, center.lat]);

  // Ensure the rectangle dimensions are within the map's bounds
  const mapBounds = map.getBounds();
  const maxLng = mapBounds.getEast();
  const minLng = mapBounds.getWest();
  const maxLat = mapBounds.getNorth();
  const minLat = mapBounds.getSouth();

  // Shift this left to account for the new selections panel taking up the right 264px of the screen.
  const leftShiftedCenterPoint = {
    x: centerPoint.x - 264 / 2,
    y: centerPoint.y,
  };

  // Adjust the top-right and bottom-left points to be within bounds
  const newTopRightPoint = {
    x: Math.min(
      leftShiftedCenterPoint.x + rectangleWidth / normalizedScallingFacter,
      map.getCanvas().width
    ),
    y: Math.max(
      Math.min(
        leftShiftedCenterPoint.y - rectangleHeight / normalizedScallingFacter,
        map.getCanvas().height
      ),
      0
    ),
  } as PointLike;

  const newBottomLeftPoint = {
    x: Math.max(
      leftShiftedCenterPoint.x - rectangleWidth / normalizedScallingFacter,
      0
    ),
    y: Math.min(
      leftShiftedCenterPoint.y + rectangleHeight / normalizedScallingFacter,
      map.getCanvas().height
    ),
  } as PointLike;

  // Convert the new points back to geographical coordinates
  const newTopRight = map.unproject(newTopRightPoint);
  const newBottomLeft = map.unproject(newBottomLeftPoint);

  // Ensure the geographical coordinates are within the map's bounds
  const boundedTopRight = {
    lat: Math.min(Math.max(newTopRight.lat, minLat), maxLat),
    lng: Math.min(Math.max(newTopRight.lng, minLng), maxLng),
  };

  const boundedBottomLeft = {
    lat: Math.min(Math.max(newBottomLeft.lat, minLat), maxLat),
    lng: Math.min(Math.max(newBottomLeft.lng, minLng), maxLng),
  };

  // Create a polygon with the bounded coordinates
  const boundsPolygon = [
    [boundedTopRight.lng, boundedTopRight.lat],
    [boundedBottomLeft.lng, boundedTopRight.lat],
    [boundedBottomLeft.lng, boundedBottomLeft.lat],
    [boundedTopRight.lng, boundedBottomLeft.lat],
    [boundedTopRight.lng, boundedTopRight.lat],
  ];

  const boundsPolygonLayer = {
    geometry: {
      coordinates: [boundsPolygon],
      type: 'Polygon',
    } as Polygon,
    properties: {},
    type: 'Feature',
  } as Feature<Geometry, GeoJsonProperties>;

  const aerialMapSource = map.getSource(
    aerialMapboxLayerSourceId
  ) as GeoJSONSource;
  if (!aerialMapSource) return null;

  aerialMapSource.setData({
    features: [boundsPolygonLayer],
    type: 'FeatureCollection',
  });

  const aerialMapBackgroundSource = map.getSource(
    aerialMapboxBackgroundLayerSourceId
  ) as GeoJSONSource;
  if (!aerialMapBackgroundSource) return null;

  const backgroundPolygon = {
    geometry: {
      coordinates: [fullScreenPolygon[0], boundsPolygon], // Polygon w/ hole. The hole being the box we want to get a screenshot of.
      type: 'Polygon',
    } as Polygon,
    properties: {},
    type: 'Feature',
  } as Feature<Geometry, GeoJsonProperties>;

  aerialMapBackgroundSource.setData({
    features: [backgroundPolygon],
    type: 'FeatureCollection',
  });

  return [newBottomLeft, newTopRight];
};

export const createImageFromMapBounds = async (
  bottomLeft: Point,
  topRight: Point,
  selectedOrientation: Orientation
) => {
  const canvas = await html2canvas(document.body);

  // Adjust for device pixel ratio (DPR)
  const pixelRatio = window.devicePixelRatio || 1;

  // Create a new canvas with the size of the rectangle area, adjusted for DPR
  const rectCanvas = document.createElement('canvas');
  const width = (topRight.x - bottomLeft.x) * pixelRatio; // Adjust width for DPR
  const height = (bottomLeft.y - topRight.y) * pixelRatio; // Adjust height for DPR
  rectCanvas.width = width;
  rectCanvas.height = height;

  const rectContext = rectCanvas.getContext('2d');
  if (!rectContext) return null;

  // Draw the portion of the map that corresponds to the rectangle
  // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage#examples
  rectContext.drawImage(
    canvas,
    bottomLeft.x * pixelRatio, // Source x, y adjusted for DPR
    topRight.y * pixelRatio,
    width, // Source width, height
    height,
    0, // Destination x, y
    0,
    width, // Destination width, height
    height
  );

  // Convert to data URL
  const dataUrl = rectCanvas.toDataURL('image/webp');

  // resize the image and make into a File
  const imageAsFile = await convertImageToFile(dataUrl, selectedOrientation);

  // Remove new canvas
  rectCanvas.remove();

  return imageAsFile;
};
